import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLaunchQA, canPromoteToLaunchReady, type LaunchQAItem, type QAStatus, type ReviewRow } from "@/hooks/useLaunchQA";
import PremiumCard from "@/components/premium/PremiumCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, ExternalLink, CheckCircle2, AlertTriangle, ShieldAlert, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

type FilterKey = "all" | "needs_review" | "needs_improvement" | "launch_ready" | "lesson" | "simulator";

const STATUS_COLOR: Record<string, string> = {
  launch_ready: "text-emerald-500",
  needs_review: "text-amber-400",
  needs_improvement: "text-red-500",
  approved: "text-sky-400",
  draft: "text-muted-foreground",
};

const SCORE_FIELDS: { key: keyof ReviewRow; label: string; appliesTo?: "lesson" | "simulator" }[] = [
  { key: "score_clarity", label: "Clarity *" },
  { key: "score_teen_engagement", label: "Teen engagement *" },
  { key: "score_real_world", label: "Real-world relevance *" },
  { key: "score_decision_quality", label: "Decision quality" },
  { key: "score_quiz_quality", label: "Quiz quality (if quiz)" },
  { key: "score_simulation_quality", label: "Simulation quality *", appliesTo: "simulator" },
  { key: "score_adaptive_tagging", label: "Adaptive tagging" },
  { key: "score_parent_insight", label: "Parent insight usefulness *" },
  { key: "score_instructor_voice", label: "Instructor voice" },
  { key: "score_safety", label: "Safety / compliance *" },
  { key: "score_mobile_readability", label: "Mobile readability *" },
  { key: "score_completion_time", label: "Completion time accuracy" },
  { key: "score_first_session_value", label: "First-session value" },
  { key: "score_beats_generic_ai", label: "Beats generic AI / free content *" },
];

function track(name: string, props?: Record<string, AnyValue>) {
  try {
    (window as AnyValue).gtag?.("event", name, props ?? {});
  } catch { /* non-critical: failure is safe to ignore here */ }
}

function previewHref(item: LaunchQAItem) {
  return item.item_type === "lesson" ? `/lesson/${item.ref_id}` : `/modules/${item.ref_id}`;
}

export default function LaunchQAReview() {
  const { items, loading, error, stats, refetch, saveReview, changeStatus } = useLaunchQA();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const { toast } = useToast();

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filter === "all") return true;
      if (filter === "lesson" || filter === "simulator") return it.item_type === filter;
      return it.qa_status === filter;
    });
  }, [items, filter]);

  const open = items.find((i) => i.id === openId) ?? null;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <ShieldAlert className="w-8 h-8 text-destructive" />
        <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
        <Button onClick={refetch} variant="outline" size="sm">Retry</Button>
      </div>
    );
  }

  const blockerCount = items.filter((i) => i.qa_status !== "launch_ready").length;

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin/founder-readiness" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Founder Readiness
            </Link>
            <h1 className="text-xl sm:text-2xl font-heading font-semibold mt-1">Launch QA Review</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Manually review the 13 Launch Path items. Promotion to <span className="font-medium">launch_ready</span> requires meeting the rubric — nothing is auto-approved.
            </p>
          </div>
          <Button onClick={refetch} variant="outline" size="sm">
            <RotateCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <PremiumCard>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-xs">
            <Stat label="Total" value={stats.total} />
            <Stat label="Launch ready" value={stats.launch_ready} tone={stats.launch_ready > 0 ? "good" : "warn"} />
            <Stat label="Needs review" value={stats.needs_review} tone={stats.needs_review > 0 ? "warn" : "good"} />
            <Stat label="Needs improvement" value={stats.needs_improvement} tone={stats.needs_improvement > 0 ? "bad" : "good"} />
            <Stat label="Avg QA score" value={stats.avg_score || "—"} />
            <Stat label="Blockers" value={blockerCount} tone={blockerCount > 0 ? "bad" : "good"} />
          </div>
        </PremiumCard>

        <PremiumCard className="bg-amber-500/5 border-amber-500/30">
          <p className="text-xs text-foreground/90 leading-relaxed">
            <span className="font-semibold">Reviewer standard:</span> Launch-ready does not mean functional. It means this item is clear, teen-friendly, real-world useful, parent-connected, safe, mobile-readable, and meaningfully better than what a teen could get from ChatGPT, YouTube, or a free worksheet.
          </p>
          <details className="mt-2 text-xs text-muted-foreground">
            <summary className="cursor-pointer">Examples of safe vs unsafe parent insights</summary>
            <div className="mt-2 space-y-2">
              <p><span className="text-emerald-400">Good:</span> "Your teen practiced opportunity cost by choosing between spending now and saving for a larger goal. They are beginning to understand tradeoffs."</p>
              <p><span className="text-red-400">Unsafe:</span> "Your teen is impulsive with money." / "Your child is financially irresponsible."</p>
            </div>
          </details>
        </PremiumCard>

        <div className="flex gap-1 overflow-x-auto text-xs">
          {(["all", "needs_review", "needs_improvement", "launch_ready", "lesson", "simulator"] as FilterKey[]).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full whitespace-nowrap border transition ${
                filter === k
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card/40 border-border/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              {k.replace(/_/g, " ")}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {filtered.map((it) => {
            const gate = canPromoteToLaunchReady(it, it.latest_review ?? null);
            return (
              <PremiumCard key={it.id} className="!p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>#{it.position}</span>
                      <span className="uppercase tracking-wide">{it.item_type}</span>
                      <span className={STATUS_COLOR[it.qa_status]}>{it.qa_status.replace(/_/g, " ")}</span>
                      {it.qa_score != null && <span>· avg {Number(it.qa_score).toFixed(1)}</span>}
                    </div>
                    <h2 className="text-sm font-heading font-semibold mt-0.5">{it.title}</h2>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {it.primary_competency || "—"} · {it.estimated_minutes ?? "?"} min
                    </p>
                    {!gate.ok && (
                      <p className="text-[10px] text-amber-400 mt-1">Blockers: {gate.missing.slice(0, 3).join(", ")}{gate.missing.length > 3 ? "…" : ""}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                    <a href={previewHref(it)} target="_blank" rel="noreferrer" className="text-[11px] text-primary inline-flex items-center gap-1">
                      Preview <ExternalLink className="w-3 h-3" />
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setOpenId(it.id);
                        track("launch_qa_item_viewed", { position: it.position, type: it.item_type });
                      }}
                    >
                      Score / Review
                    </Button>
                  </div>
                </div>
              </PremiumCard>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-xs text-muted-foreground py-8">No items match this filter.</p>
          )}
        </div>
      </div>

      {open && (
        <ReviewPanel
          item={open}
          onClose={() => setOpenId(null)}
          onSave={async (payload) => {
            try {
              await saveReview(open.id, payload);
              track("launch_qa_score_saved", { position: open.position, type: open.item_type });
              toast({ title: "Review saved" });
            } catch (e: unknown) {
              toast({ title: "Save failed", description: getErrorMessage(e), variant: "destructive" });
            }
          }}
          onTransition={async (status, notes) => {
            try {
              await changeStatus(open, status, notes);
              track("launch_qa_status_changed", { position: open.position, new_status: status });
              if (status === "launch_ready") track("launch_qa_item_promoted", { position: open.position });
              if (status === "needs_improvement") track("launch_qa_item_marked_needs_improvement", { position: open.position });
              toast({ title: `Status → ${status.replace(/_/g, " ")}` });
              setOpenId(null);
            } catch (e: unknown) {
              toast({ title: "Status change blocked", description: getErrorMessage(e), variant: "destructive" });
            }
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: "good" | "warn" | "bad" }) {
  const toneCls = tone === "good" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : tone === "bad" ? "text-red-400" : "text-foreground";
  return (
    <div className="bg-card/40 rounded-lg px-2 py-2">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className={`text-sm font-semibold tabular-nums ${toneCls}`}>{value}</p>
    </div>
  );
}

function ReviewPanel({
  item,
  onClose,
  onSave,
  onTransition,
}: {
  item: LaunchQAItem;
  onClose: () => void;
  onSave: (payload: Partial<ReviewRow>) => Promise<void>;
  onTransition: (status: QAStatus, notes?: string) => Promise<void>;
}) {
  const prev = item.latest_review;
  const [scores, setScores] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of SCORE_FIELDS) {
      const v = prev?.[f.key] as number | null | undefined;
      init[String(f.key)] = v != null ? String(v) : "";
    }
    return init;
  });
  const [notes, setNotes] = useState(prev?.reviewer_notes ?? "");
  const [fixes, setFixes] = useState(prev?.required_fixes ?? "");
  const [transitionNotes, setTransitionNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const payload: Partial<ReviewRow> = useMemo(() => {
    const out: AnyValue = { reviewer_notes: notes, required_fixes: fixes };
    for (const f of SCORE_FIELDS) {
      const raw = scores[String(f.key)];
      if (raw === "" || raw == null) {
        out[f.key] = null;
      } else {
        const n = Math.max(1, Math.min(5, Number(raw)));
        out[f.key] = Number.isFinite(n) ? n : null;
      }
    }
    return out;
  }, [scores, notes, fixes]);

  // Build a hypothetical review for gate check
  const hypothetical: ReviewRow = { ...(prev ?? ({} as ReviewRow)), ...(payload as ReviewRow) };
  const gate = canPromoteToLaunchReady(item, hypothetical);

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <PremiumCard>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                #{item.position} · {item.item_type} · <span className={STATUS_COLOR[item.qa_status]}>{item.qa_status.replace(/_/g, " ")}</span>
              </p>
              <h2 className="text-lg font-heading font-semibold">{item.title}</h2>
              <a href={previewHref(item)} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1">
                Open preview <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs mb-4">
            <Meta label="Primary competency" value={item.primary_competency} />
            <Meta label="Estimated minutes" value={item.estimated_minutes} />
            <Meta label="Parent insight hook" value={item.parent_insight_hook} long />
            <Meta label="Parent conversation prompt" value={item.parent_conversation_prompt} long />
          </div>

          <h3 className="text-sm font-heading font-semibold mb-2">Scoring (1–5, * = required ≥4 for launch_ready)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {SCORE_FIELDS.filter((f) => !f.appliesTo || f.appliesTo === item.item_type).map((f) => (
              <label key={String(f.key)} className="text-[11px]">
                <span className="text-muted-foreground">{f.label}</span>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={scores[String(f.key)]}
                  onChange={(e) => setScores((s) => ({ ...s, [String(f.key)]: e.target.value }))}
                  className="h-8 text-sm mt-0.5"
                />
              </label>
            ))}
          </div>

          <label className="text-[11px] block mb-3">
            <span className="text-muted-foreground">Reviewer notes (required for launch_ready)</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-0.5" placeholder="What works? What is the differentiator vs generic AI / YouTube?" />
          </label>
          <label className="text-[11px] block mb-4">
            <span className="text-muted-foreground">Required fixes (if any)</span>
            <Textarea value={fixes} onChange={(e) => setFixes(e.target.value)} rows={2} className="mt-0.5" placeholder="Specific edits needed before launch_ready." />
          </label>

          <div className="flex items-center justify-end gap-2 border-t border-border/30 pt-3">
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                await onSave(payload);
                setSaving(false);
              }}
            >
              Save review
            </Button>
          </div>

          <div className="mt-4 border-t border-border/30 pt-3 space-y-3">
            <h3 className="text-sm font-heading font-semibold">Status transitions</h3>
            <Textarea
              value={transitionNotes}
              onChange={(e) => setTransitionNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes on this status change"
              className="text-xs"
            />
            {!gate.ok && (
              <div className="text-[11px] text-amber-400 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Cannot promote to launch_ready yet.</p>
                  <p className="text-muted-foreground">Missing: {gate.missing.join(", ")}</p>
                  <p className="text-muted-foreground italic mt-1">Save your latest scores before promoting — gate uses persisted review.</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onTransition("needs_review", transitionNotes)}
                disabled={item.qa_status === "needs_review"}
              >
                Mark Needs Review
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onTransition("needs_improvement", transitionNotes)}
                disabled={item.qa_status === "needs_improvement"}
              >
                Mark Needs Improvement
              </Button>
              <Button
                size="sm"
                onClick={() => onTransition("launch_ready", transitionNotes)}
                disabled={!canPromoteToLaunchReady(item, item.latest_review ?? null).ok || item.qa_status === "launch_ready"}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Promote to Launch Ready
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Promotion uses the most recently saved review. Save scores first, then promote.
            </p>
          </div>
        </PremiumCard>
      </div>
    </div>
  );
}

function Meta({ label, value, long }: { label: string; value: AnyValue; long?: boolean }) {
  return (
    <div className="bg-card/40 rounded-lg px-3 py-2">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className={`text-xs ${long ? "leading-snug" : "font-medium"} ${value ? "" : "text-muted-foreground italic"}`}>{value || "missing"}</p>
    </div>
  );
}